import {useCallback, useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link, useNavigate, useSearchParams} from 'react-router';
import {meQueryOptions, useLogout} from '../api/auth';
import {useUploadQueue} from '../api/files';
import {Dropzone} from '../components/Dropzone';
import {UploadQueue} from '../components/UploadQueue';
import {useWakeLock} from '../lib/useWakeLock';

export function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const me = useQuery(meQueryOptions);
  const logout = useLogout();

  // anketaId передається ботом через deep-link (`/app?anketaId=1234567`).
  // Без нього аплоад заблокований. Парсимо як number; невалідний рядок → null.
  const rawAnketaId = searchParams.get('anketaId');
  const parsedAnketaId =
    rawAnketaId !== null && /^\d+$/.test(rawAnketaId) ? Number(rawAnketaId) : null;

  // Перевіряємо чи у юзера справді є папка під цю анкету — інакше аплоад
  // одразу впаде з 404 на бекенді. Краще показати дружнє повідомлення.
  const activeFolder =
    parsedAnketaId !== null
      ? me.data?.folders.find((f) => f.anketaId === parsedAnketaId) ?? null
      : null;
  const anketaIdForUpload = activeFolder ? activeFolder.anketaId : null;

  const queue = useUploadQueue(anketaIdForUpload);

  const hasUploadsInFlight = useMemo(
    () => queue.items.some((i) => i.status === 'queued' || i.status === 'uploading'),
    [queue.items],
  );

  useWakeLock(hasUploadsInFlight);

  const onDrop = useCallback(
    (accepted: File[]) => queue.enqueue(accepted),
    [queue],
  );

  const handleLogout = (): void => {
    logout.mutate(undefined, {
      onSettled: () => navigate('/', {replace: true}),
    });
  };

  const displayName = me.data?.firstName ?? me.data?.usernameLocked ?? '';

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold truncate">{displayName}</h1>
            {me.data && (
              <p className="text-sm text-slate-500 truncate">@{me.data.usernameLocked}</p>
            )}
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {me.data?.isAdmin && (
              <Link to="/admin" className="text-sm text-slate-600 hover:text-slate-900">
                Admin
              </Link>
            )}
            <button
              type="button"
              onClick={handleLogout}
              disabled={logout.isPending}
              className="text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Выйти
            </button>
          </div>
        </header>

        {anketaIdForUpload !== null && activeFolder && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
            Загрузка в анкету:{' '}
            <span className="font-medium text-slate-900">
              {activeFolder.anketaName}@{activeFolder.anketaId}
            </span>
          </div>
        )}
        {parsedAnketaId !== null && activeFolder === null && (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            У вас нет папки для анкеты {parsedAnketaId}. Запросите её через бота.
          </div>
        )}

        {anketaIdForUpload !== null && <Dropzone onDrop={onDrop} />}

        <UploadQueue items={queue.items} onCancel={queue.cancel} onRetry={queue.retry} />
      </div>
    </div>
  );
}
